import React, { useState, useEffect } from "react";
import axios from "axios";
import { MapContainer, TileLayer, Marker, Popup, Polyline, LayersControl, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import {
  MapPin,
  Layers,
  Search,
  Filter,
  Activity,
  Phone,
  Radio,
  Building,
  CheckCircle,
  AlertTriangle,
  Plus,
  Route,
  Trash2,
  X,
  Undo2
} from "lucide-react";

// Custom HTML Leaflet Icons
const createPopIcon = () =>
  L.divIcon({
    className: "custom-leaflet-icon",
    html: `<div style="background: linear-gradient(135deg, #0284c7, #2563eb); width: 34px; height: 34px; border-radius: 10px; border: 2px solid #ffffff; display: flex; align-items: center; justify-content: center; box-shadow: 0 0 15px rgba(37,99,235,0.7); color: white; font-size: 14px; font-weight: bold;">POP</div>`,
    iconSize: [34, 34],
    iconAnchor: [17, 17]
  });

const createOdpIcon = (kode) =>
  L.divIcon({
    className: "custom-leaflet-icon",
    html: `<div style="background: linear-gradient(135deg, #d97706, #b45309); width: 28px; height: 28px; border-radius: 8px; border: 2px solid #ffffff; display: flex; align-items: center; justify-content: center; box-shadow: 0 0 12px rgba(217,119,6,0.6); color: white; font-size: 10px; font-weight: 800;">ODP</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14]
  });

const createCustomerIcon = (status) => {
  const isOnline = status === "aktif";
  const bg = isOnline ? "#10b981" : "#ef4444";
  return L.divIcon({
    className: "custom-leaflet-icon",
    html: `<div style="background: ${bg}; width: 20px; height: 20px; border-radius: 50%; border: 2px solid #ffffff; box-shadow: 0 0 10px ${bg};"></div>`,
    iconSize: [20, 20],
    iconAnchor: [10, 10]
  });
};

const FlyToLocation = ({ position }) => {
  const map = useMap();
  useEffect(() => { if (position) map.flyTo(position, 17, { duration: 1.2 }); }, [position, map]);
  return null;
};

const createDrawPointIcon = () =>
  L.divIcon({
    className: "custom-leaflet-icon",
    html: `<div style="background:#facc15;width:12px;height:12px;border-radius:50%;border:2px solid #ffffff;box-shadow:0 0 8px rgba(250,204,21,0.8);"></div>`,
    iconSize: [12, 12],
    iconAnchor: [6, 6]
  });

const MapClickHandler = ({ onMapClick }) => {
  useMapEvents({ click: (e) => onMapClick([e.latlng.lat, e.latlng.lng]) });
  return null;
};

export const JaringanMapsPage = () => {
  const [data, setData] = useState({
    pop_nodes: [],
    odps: [],
    customers: [],
    jalur: [],
    center: [-6.9144, 107.6098]
  });
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [search, setSearch] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const [myLoc, setMyLoc] = useState(null);
  const [locating, setLocating] = useState(false);

  // Checklist layer peta
  const [layers, setLayers] = useState({ pop: true, odp: true, pelanggan: true });

  // Mode tambah POP manual (klik peta)
  const [addPopMode, setAddPopMode] = useState(false);
  const [popDraft, setPopDraft] = useState(null);
  const [popForm, setPopForm] = useState({ nama: "", bandwidth: "", keterangan: "" });
  const [savingPop, setSavingPop] = useState(false);

  // Mode gambar garis jalur (polyline)
  const [drawMode, setDrawMode] = useState(false);
  const [drawPoints, setDrawPoints] = useState([]);
  const [showJalurForm, setShowJalurForm] = useState(false);
  const [jalurForm, setJalurForm] = useState({ nama: "", tipe: "backbone" });
  const [savingJalur, setSavingJalur] = useState(false);

  const handleLokasiTerkini = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { setMyLoc([pos.coords.latitude, pos.coords.longitude]); setLocating(false); },
      () => { alert("Gagal mendapatkan lokasi. Izinkan akses lokasi di browser."); setLocating(false); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

  const fetchMapsData = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/jaringan/maps`, {
        withCredentials: true
      });
      setData(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await fetchMapsData();
      setLoading(false);
    })();
  }, []);

  const handleMapClick = (latlng) => {
    if (addPopMode) {
      setPopDraft({ lat: latlng[0], lng: latlng[1] });
      setPopForm({ nama: "", bandwidth: "", keterangan: "" });
      setAddPopMode(false);
      return;
    }
    if (drawMode) {
      setDrawPoints((pts) => [...pts, latlng]);
    }
  };

  const handleSavePop = async () => {
    if (!popDraft || !popForm.nama.trim()) return;
    try {
      setSavingPop(true);
      await axios.post(`${BACKEND_URL}/api/jaringan/pop`, {
        nama: popForm.nama.trim(),
        bandwidth: popForm.bandwidth.trim(),
        keterangan: popForm.keterangan.trim(),
        lat: popDraft.lat,
        lng: popDraft.lng
      }, { withCredentials: true });
      setPopDraft(null);
      await fetchMapsData();
    } catch (err) {
      alert(err.response?.data?.detail || "Gagal menyimpan POP");
    } finally {
      setSavingPop(false);
    }
  };

  const handleDeletePop = async (popId) => {
    if (!window.confirm("Hapus titik POP ini dari peta?")) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/jaringan/pop/${popId}`, { withCredentials: true });
      await fetchMapsData();
    } catch (err) {
      alert(err.response?.data?.detail || "Gagal menghapus POP");
    }
  };

  const handleSaveJalur = async () => {
    if (drawPoints.length < 2 || !jalurForm.nama.trim()) return;
    try {
      setSavingJalur(true);
      await axios.post(`${BACKEND_URL}/api/jaringan/jalur`, {
        nama: jalurForm.nama.trim(),
        tipe: jalurForm.tipe,
        coords: drawPoints
      }, { withCredentials: true });
      setDrawPoints([]);
      setDrawMode(false);
      setShowJalurForm(false);
      setJalurForm({ nama: "", tipe: "backbone" });
      await fetchMapsData();
    } catch (err) {
      alert(err.response?.data?.detail || "Gagal menyimpan jalur");
    } finally {
      setSavingJalur(false);
    }
  };

  const handleDeleteJalur = async (jalurId) => {
    if (!window.confirm("Hapus garis jalur ini?")) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/jaringan/jalur/${jalurId}`, { withCredentials: true });
      await fetchMapsData();
    } catch (err) {
      alert(err.response?.data?.detail || "Gagal menghapus jalur");
    }
  };

  const cancelDraw = () => {
    setDrawMode(false);
    setDrawPoints([]);
    setShowJalurForm(false);
  };

  const filteredCustomers = (data.customers || []).filter((c) => {
    const matchesSearch =
      c.nama?.toLowerCase().includes(search.toLowerCase()) ||
      c.no_layanan?.toLowerCase().includes(search.toLowerCase()) ||
      c.odp_kode?.toLowerCase().includes(search.toLowerCase());
    if (filterStatus === "ALL") return matchesSearch;
    return matchesSearch && c.status === filterStatus;
  });

  return (
    <div className="space-y-4" data-testid="maps-page">
      {/* Top Map Filter Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-cyan-500/20 text-cyan-400 flex items-center justify-center">
            <MapPin className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-base sm:text-lg font-bold text-white">
              Peta Sebaran Fiber Optik & Pelanggan
            </h1>
            <p className="text-xs text-slate-400">
              POP Data Center, Box ODP Distribusi & Titik Drop Core Rumah
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Status filter buttons */}
          <div className="flex bg-slate-900 border border-slate-700 rounded-xl p-1 text-xs">
            <button
              onClick={() => setFilterStatus("ALL")}
              className={`px-3 py-1 rounded-lg font-semibold transition-colors ${
                filterStatus === "ALL"
                  ? "bg-cyan-600 text-white"
                  : "text-slate-400 hover:text-white"
              }`}
            >
              Semua
            </button>
            <button
              onClick={() => setFilterStatus("aktif")}
              className={`px-3 py-1 rounded-lg font-semibold transition-colors ${
                filterStatus === "aktif"
                  ? "bg-emerald-600 text-white"
                  : "text-slate-400 hover:text-white"
              }`}
            >
              Aktif
            </button>
            <button
              onClick={() => setFilterStatus("isolir")}
              className={`px-3 py-1 rounded-lg font-semibold transition-colors ${
                filterStatus === "isolir"
                  ? "bg-rose-600 text-white"
                  : "text-slate-400 hover:text-white"
              }`}
            >
              Isolir
            </button>
          </div>

          {/* Search box */}
          <div className="relative w-full sm:w-48">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Cari titik/pelanggan..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-1.5 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
            />
          </div>
        </div>
      </div>

      {/* Layer checklist + alat gambar peta */}
      <div className="flex flex-wrap items-center gap-2 p-3 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl" data-testid="map-layer-bar">
        <span className="text-[10px] font-bold text-slate-400 uppercase flex items-center gap-1">
          <Layers className="w-3.5 h-3.5" /> Layer:
        </span>
        {[
          { key: "pop", label: "POP", accent: "accent-blue-500", testid: "layer-pop-check" },
          { key: "odp", label: "ODP", accent: "accent-amber-500", testid: "layer-odp-check" },
          { key: "pelanggan", label: "Pelanggan", accent: "accent-emerald-500", testid: "layer-pelanggan-check" },
        ].map((l) => (
          <label
            key={l.key}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-slate-900 border border-slate-700 cursor-pointer text-xs text-slate-200 font-semibold hover:border-slate-500 transition-colors"
          >
            <input
              type="checkbox"
              checked={layers[l.key]}
              onChange={() => setLayers((v) => ({ ...v, [l.key]: !v[l.key] }))}
              className={`w-3.5 h-3.5 ${l.accent}`}
              data-testid={l.testid}
            />
            {l.label}
          </label>
        ))}

        <div className="flex-1" />

        <button
          onClick={() => { setAddPopMode((v) => !v); setDrawMode(false); setDrawPoints([]); setPopDraft(null); }}
          data-testid="btn-add-pop"
          className={`px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 border transition-colors ${
            addPopMode
              ? "bg-blue-600 text-white border-blue-500 animate-pulse"
              : "bg-slate-900 text-blue-300 border-slate-700 hover:border-blue-500"
          }`}
        >
          <Plus className="w-3.5 h-3.5" />
          {addPopMode ? "Klik peta untuk titik POP..." : "Tambah POP"}
        </button>
        <button
          onClick={() => { setDrawMode((v) => !v); setAddPopMode(false); setDrawPoints([]); setPopDraft(null); }}
          data-testid="btn-draw-jalur"
          className={`px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 border transition-colors ${
            drawMode
              ? "bg-amber-600 text-white border-amber-500"
              : "bg-slate-900 text-amber-300 border-slate-700 hover:border-amber-500"
          }`}
        >
          <Route className="w-3.5 h-3.5" />
          {drawMode ? "Mode Gambar Aktif" : "Gambar Jalur"}
        </button>
      </div>

      {/* Main Map Box */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Leaflet Map Frame */}
        <div className="lg:col-span-3 h-[560px] rounded-2xl overflow-hidden border border-slate-800 shadow-2xl relative bg-[#090e1a]">
          {!loading && (
            <MapContainer
              center={data.center || [-6.9100, 107.6190]}
              zoom={13}
              scrollWheelZoom={true}
              className="h-full w-full"
            >
              {/* Pilihan basemap: Peta Jalan / Satelit */}
              <LayersControl position="topright">
                <LayersControl.BaseLayer checked name="Peta Jalan">
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                    maxZoom={19}
                  />
                </LayersControl.BaseLayer>
                <LayersControl.BaseLayer name="Satelit Terbaru (Esri)">
                  <TileLayer
                    attribution="Tiles &copy; Esri — Source: Esri, Maxar, Earthstar Geographics"
                    url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                    maxZoom={19}
                  />
                </LayersControl.BaseLayer>
              </LayersControl>
              <FlyToLocation position={myLoc} />
              <MapClickHandler onMapClick={handleMapClick} />

              {/* Garis Jalur (backbone/ODP/pelanggan) hasil gambar manual */}
              {(data.jalur || []).map((j) => (
                <Polyline
                  key={`jalur-${j.id}`}
                  positions={j.coords}
                  pathOptions={{
                    color: j.tipe === "backbone" ? "#0284c7" : j.tipe === "odp" ? "#d97706" : "#10b981",
                    weight: j.tipe === "backbone" ? 4 : j.tipe === "odp" ? 3 : 2,
                    dashArray: j.tipe === "pelanggan" ? "4, 6" : undefined,
                    opacity: 0.85
                  }}
                >
                  <Popup>
                    <div className="p-1 space-y-1 text-slate-100">
                      <h4 className="font-bold text-cyan-400 text-xs">{j.nama}</h4>
                      <p className="text-[11px] text-slate-300">
                        Tipe: {j.tipe === "backbone" ? "Backbone" : j.tipe === "odp" ? "Jalur ODP" : "Jalur Pelanggan"}
                      </p>
                      <p className="text-[10px] text-slate-400">{j.coords?.length || 0} titik koordinat</p>
                      <button
                        onClick={() => handleDeleteJalur(j.id)}
                        data-testid={`delete-jalur-${j.id}`}
                        className="mt-1 px-2 py-0.5 bg-rose-600 hover:bg-rose-500 text-white text-[10px] font-bold rounded"
                      >
                        Hapus Jalur
                      </button>
                    </div>
                  </Popup>
                </Polyline>
              ))}

              {/* Preview garis & titik saat mode gambar */}
              {drawPoints.length > 0 && (
                <Polyline
                  positions={drawPoints}
                  pathOptions={{ color: "#facc15", weight: 3, dashArray: "6, 6", opacity: 0.9 }}
                />
              )}
              {drawPoints.map((p, i) => (
                <Marker key={`draw-${i}`} position={p} icon={createDrawPointIcon()} interactive={false} />
              ))}

              {/* POP Markers */}
              {layers.pop && (data.pop_nodes || []).map((pop) => (
                <Marker
                  key={pop.id}
                  position={[pop.lat, pop.lng]}
                  icon={createPopIcon()}
                >
                  <Popup>
                    <div className="p-1 space-y-1 text-slate-100">
                      <h4 className="font-bold text-cyan-400 text-xs">{pop.nama}</h4>
                      <p className="text-[11px] text-slate-300">
                        Type: Core POP Backbone
                      </p>
                      {pop.bandwidth && (
                        <p className="text-[11px] text-slate-300">
                          Bandwidth Uplink: {pop.bandwidth}
                        </p>
                      )}
                      {pop.keterangan && (
                        <p className="text-[10px] text-slate-400">{pop.keterangan}</p>
                      )}
                      <div className="flex items-center gap-2 pt-1">
                        <span className="inline-block px-2 py-0.5 rounded bg-blue-500/30 text-blue-300 text-[10px] font-bold">
                          STATUS: ACTIVE
                        </span>
                        <button
                          onClick={() => handleDeletePop(pop.id)}
                          data-testid={`delete-pop-${pop.id}`}
                          className="px-2 py-0.5 bg-rose-600 hover:bg-rose-500 text-white text-[10px] font-bold rounded flex items-center gap-1"
                        >
                          <Trash2 className="w-3 h-3" /> Hapus
                        </button>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              ))}

              {/* ODP Markers */}
              {layers.odp && (data.odps || []).map((odp) => (
                <Marker
                  key={odp.id || odp.kode}
                  position={[odp.lat, odp.lng]}
                  icon={createOdpIcon(odp.kode)}
                >
                  <Popup>
                    <div className="p-1 space-y-1 text-slate-100">
                      <h4 className="font-bold text-amber-400 text-xs">
                        {odp.kode} - {odp.nama}
                      </h4>
                      <p className="text-[11px] text-slate-300">Area: {odp.area}</p>
                      <p className="text-[11px] text-slate-300">
                        Port Terpakai: {odp.terpakai} / {odp.kapasitas} Port
                      </p>
                      <p className="text-[10px] text-slate-400">
                        Uplink: {odp.olt_uplink}
                      </p>
                    </div>
                  </Popup>
                </Marker>
              ))}

              {/* Customer Markers */}
              {layers.pelanggan && filteredCustomers.map((cust) => (
                <Marker
                  key={cust.id}
                  position={[cust.lat, cust.lng]}
                  icon={createCustomerIcon(cust.status)}
                  eventHandlers={{
                    click: () => setSelectedItem(cust)
                  }}
                >
                  <Popup>
                    <div className="p-1 space-y-1 text-slate-100">
                      <h4 className="font-bold text-white text-xs">{cust.nama}</h4>
                      <p className="text-[10px] text-slate-400 font-mono">
                        {cust.no_layanan}
                      </p>
                      <p className="text-[11px] text-slate-300">{cust.alamat}</p>
                      <p className="text-[11px] text-cyan-300 font-semibold">
                        Paket: {cust.paket}
                      </p>
                      <div className="flex justify-between items-center pt-1">
                        <span
                          className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                            cust.status === "aktif"
                              ? "bg-emerald-500/20 text-emerald-400"
                              : "bg-rose-500/20 text-rose-400"
                          }`}
                        >
                          {cust.status.toUpperCase()}
                        </span>
                        <span className="text-[10px] text-slate-400 font-mono">
                          {cust.optical_power} dBm
                        </span>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              ))}

              {/* Marker Lokasi Terkini */}
              {myLoc && (
                <Marker position={myLoc} icon={createCustomerIcon("aktif")}>
                  <Popup>
                    <div className="p-1 text-slate-100 text-xs font-bold">Lokasi Anda Saat Ini</div>
                  </Popup>
                </Marker>
              )}
            </MapContainer>
          )}

          {/* Tombol Lokasi Terkini */}
          <button
            type="button"
            onClick={handleLokasiTerkini}
            disabled={locating}
            data-testid="btn-lokasi-terkini"
            className="absolute top-4 left-4 z-[1000] px-3 py-2 rounded-xl bg-slate-900/90 backdrop-blur-md border border-cyan-500/40 text-cyan-300 text-[11px] font-bold shadow-lg hover:bg-cyan-500/20 transition-colors disabled:opacity-50"
          >
            {locating ? "Mencari lokasi..." : "◎ Lokasi Terkini"}
          </button>

          {/* Toolbar Mode Gambar Jalur */}
          {drawMode && (
            <div
              className="absolute top-4 left-1/2 -translate-x-1/2 z-[1000] px-4 py-2 rounded-xl bg-slate-900/95 backdrop-blur-md border border-amber-500/40 shadow-lg flex items-center gap-3"
              data-testid="draw-toolbar"
            >
              <span className="text-[11px] text-amber-300 font-bold whitespace-nowrap">
                Klik peta untuk menambah titik ({drawPoints.length} titik)
              </span>
              <button
                onClick={() => setDrawPoints((p) => p.slice(0, -1))}
                disabled={drawPoints.length === 0}
                data-testid="draw-undo-btn"
                title="Hapus titik terakhir"
                className="p-1.5 rounded-lg bg-slate-800 text-slate-300 hover:text-white disabled:opacity-40"
              >
                <Undo2 className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => { setJalurForm({ nama: "", tipe: "backbone" }); setShowJalurForm(true); }}
                disabled={drawPoints.length < 2}
                data-testid="draw-finish-btn"
                className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-[11px] font-bold disabled:opacity-40 whitespace-nowrap"
              >
                Selesai & Simpan
              </button>
              <button
                onClick={cancelDraw}
                data-testid="draw-cancel-btn"
                className="px-3 py-1.5 rounded-lg bg-rose-600/80 hover:bg-rose-500 text-white text-[11px] font-bold"
              >
                Batal
              </button>
            </div>
          )}

          {/* Map Legend Overlay */}
          <div className="absolute bottom-4 left-4 z-[1000] bg-slate-900/90 backdrop-blur-md border border-slate-700 p-3 rounded-xl text-[11px] text-slate-200 space-y-1.5 shadow-lg">
            <span className="font-bold text-slate-400 uppercase text-[10px] block mb-1">
              Legenda Peta
            </span>
            <div className="flex items-center gap-2">
              <div className="w-3.5 h-3.5 rounded bg-blue-600 border border-white"></div>
              <span>POP Core DC Backbone</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3.5 h-3.5 rounded bg-amber-600 border border-white"></div>
              <span>ODP Box Distribusi</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full bg-emerald-500"></div>
              <span>Pelanggan Aktif</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full bg-rose-500"></div>
              <span>Pelanggan Terisolir</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-0.5 bg-sky-600"></div>
              <span>Jalur Backbone</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-0.5 bg-amber-600"></div>
              <span>Jalur ODP</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-0 border-t-2 border-dashed border-emerald-500"></div>
              <span>Jalur Pelanggan</span>
            </div>
          </div>
        </div>

        {/* Side Inspector Panel */}
        <div className="p-4 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl flex flex-col justify-between space-y-4">
          <div>
            <h3 className="text-sm font-bold text-white border-b border-slate-800 pb-2.5 flex items-center gap-2">
              <Activity className="w-4 h-4 text-cyan-400" />
              <span>Detail Titik Terpilih</span>
            </h3>

            {selectedItem ? (
              <div className="mt-3 space-y-3 text-xs">
                <div>
                  <span className="text-[10px] text-slate-500 uppercase font-bold">
                    Nama Pelanggan
                  </span>
                  <p className="text-sm font-bold text-white mt-0.5">
                    {selectedItem.nama}
                  </p>
                  <p className="text-slate-400 font-mono text-[11px]">
                    {selectedItem.no_layanan}
                  </p>
                </div>

                <div>
                  <span className="text-[10px] text-slate-500 uppercase font-bold">
                    Alamat Lengkap
                  </span>
                  <p className="text-slate-300 mt-0.5">{selectedItem.alamat}</p>
                </div>

                <div className="grid grid-cols-2 gap-2 pt-1">
                  <div className="p-2 rounded-lg bg-slate-900 border border-slate-800">
                    <span className="text-[10px] text-slate-500 block">ODP Box</span>
                    <span className="font-bold text-amber-400">
                      {selectedItem.odp_kode}
                    </span>
                  </div>
                  <div className="p-2 rounded-lg bg-slate-900 border border-slate-800">
                    <span className="text-[10px] text-slate-500 block">Redaman Rx</span>
                    <span className="font-bold text-cyan-400 font-mono">
                      {selectedItem.optical_power} dBm
                    </span>
                  </div>
                </div>

                <div className="pt-2">
                  <span className="text-[10px] text-slate-500 uppercase font-bold block mb-1">
                    Status Layanan
                  </span>
                  <span
                    className={`inline-block px-2.5 py-1 rounded-md text-xs font-bold uppercase ${
                      selectedItem.status === "aktif"
                        ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30"
                        : "bg-rose-500/20 text-rose-300 border border-rose-500/30"
                    }`}
                  >
                    {selectedItem.status}
                  </span>
                </div>

                {selectedItem.no_hp && (
                  <div className="pt-2">
                    <a
                      href={`https://wa.me/62${selectedItem.no_hp.replace(/^0/, "")}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-bold flex items-center justify-center gap-1.5 transition-colors"
                    >
                      <Phone className="w-3.5 h-3.5" />
                      <span>Chat WhatsApp</span>
                    </a>
                  </div>
                )}
              </div>
            ) : (
              <div className="py-12 text-center text-slate-500 text-xs">
                <MapPin className="w-8 h-8 text-slate-700 mx-auto mb-2" />
                <span>Klik pada salah satu pin pelanggan di peta untuk melihat rincian jaringan & redaman.</span>
              </div>
            )}
          </div>

          <div className="p-3 bg-slate-900/80 border border-slate-800 rounded-xl text-[11px] text-slate-400">
            <span>Total ODP Aktif: </span>
            <span className="text-white font-bold">{data.odps?.length || 0} Box</span>
          </div>
        </div>
      </div>

      {/* Modal Form POP Baru */}
      {popDraft && (
        <div className="fixed inset-0 z-[2000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="pop-form-modal">
          <div className="bg-[#0f172a] border border-slate-700 rounded-2xl p-5 w-full max-w-sm space-y-3 shadow-2xl">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-white flex items-center gap-2">
                <Building className="w-4 h-4 text-blue-400" /> Tambah Titik POP
              </h3>
              <button onClick={() => setPopDraft(null)} data-testid="pop-form-close" className="text-slate-400 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-[10px] text-slate-400 font-mono">
              Koordinat: {popDraft.lat.toFixed(6)}, {popDraft.lng.toFixed(6)}
            </p>
            <div>
              <label className="block text-xs text-slate-300 mb-1">Nama POP *</label>
              <input
                value={popForm.nama}
                onChange={(e) => setPopForm({ ...popForm, nama: e.target.value })}
                data-testid="pop-nama-input"
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100"
                placeholder="cth: POP Data Center Dago"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-300 mb-1">Bandwidth Uplink</label>
              <input
                value={popForm.bandwidth}
                onChange={(e) => setPopForm({ ...popForm, bandwidth: e.target.value })}
                data-testid="pop-bandwidth-input"
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100"
                placeholder="cth: 10 Gbps"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-300 mb-1">Keterangan</label>
              <input
                value={popForm.keterangan}
                onChange={(e) => setPopForm({ ...popForm, keterangan: e.target.value })}
                data-testid="pop-keterangan-input"
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100"
                placeholder="Opsional"
              />
            </div>
            <button
              onClick={handleSavePop}
              disabled={savingPop || !popForm.nama.trim()}
              data-testid="pop-save-btn"
              className="w-full py-2.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white rounded-xl font-bold text-xs transition-colors"
            >
              {savingPop ? "Menyimpan..." : "Simpan POP"}
            </button>
          </div>
        </div>
      )}

      {/* Modal Simpan Garis Jalur */}
      {showJalurForm && (
        <div className="fixed inset-0 z-[2000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="jalur-form-modal">
          <div className="bg-[#0f172a] border border-slate-700 rounded-2xl p-5 w-full max-w-sm space-y-3 shadow-2xl">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-white flex items-center gap-2">
                <Route className="w-4 h-4 text-amber-400" /> Simpan Garis Jalur
              </h3>
              <button onClick={() => setShowJalurForm(false)} data-testid="jalur-form-close" className="text-slate-400 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-[10px] text-slate-400">{drawPoints.length} titik koordinat akan disimpan sebagai jalur.</p>
            <div>
              <label className="block text-xs text-slate-300 mb-1">Nama Jalur *</label>
              <input
                value={jalurForm.nama}
                onChange={(e) => setJalurForm({ ...jalurForm, nama: e.target.value })}
                data-testid="jalur-nama-input"
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100"
                placeholder="cth: Backbone POP - ODP Dago 01"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-300 mb-1">Tipe Jalur *</label>
              <select
                value={jalurForm.tipe}
                onChange={(e) => setJalurForm({ ...jalurForm, tipe: e.target.value })}
                data-testid="jalur-tipe-select"
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100"
              >
                <option value="backbone">Backbone (POP ke ODP/Area)</option>
                <option value="odp">Jalur ODP (Distribusi)</option>
                <option value="pelanggan">Jalur Pelanggan (Drop Core)</option>
              </select>
            </div>
            <button
              onClick={handleSaveJalur}
              disabled={savingJalur || !jalurForm.nama.trim()}
              data-testid="jalur-save-btn"
              className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white rounded-xl font-bold text-xs transition-colors"
            >
              {savingJalur ? "Menyimpan..." : "Simpan Jalur"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
